// ─── Realtime Sync Conflicts Hook (Admin) ───────────────────────────────────
//
// G2.25: Mirrors apps/pos/src/hooks/use-sync-conflicts-realtime.ts for the
// Admin Sync Health page. Two channels:
//
//   1. Platform staff (SUPER_ADMIN / ADMIN / MODERATOR) subscribe to
//      `sync:conflict:platform` — a cross-tenant fan-out emitted by
//      EventsGateway.emitSyncConflict (services/api/src/events/events.gateway.ts)
//      to the `platform:sync-health` socket room. Payload includes `tenant_id`
//      so the toast can label which tenant the conflict belongs to.
//
//   2. Tenant staff (OWNER / MANAGER) subscribe to the original `sync:conflict`
//      event (room `tenant:{id}`) so the admin variant of /sync-health stays
//      live for tenant-scoped users too. CASHIER is blocked at the route guard
//      (`minRoleLevel={30}` in router.tsx) so they never reach this hook.
//
// On every event the hook:
//   • Shows a warning toast with entity label (and tenant label for platform).
//   • Calls the page-supplied `onRefresh` callback so the visible conflict
//     table refetches. For platform events we only refresh when the event's
//     tenant matches the active tenant filter (or the picker is empty),
//     since refreshing under a non-matching filter would just re-fetch the
//     same rows.
//
// Traceability: G2.25, FR-SYNC-008, FR-SYNC-013, US-BILL-019.

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { onSocketEvent } from '@/lib/socket-client';
import { ENTITY_TYPE_CONFIG } from '@/lib/sync-constants';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Tenant-scoped event — same shape as the existing POS variant.
 * Mirrors services/api/src/events/events.gateway.ts :: emitSyncConflict.
 */
interface SyncConflictEventPayload {
  conflict_id: string;
  entity_type: string;
  entity_id?: string;
  message: string;
}

/**
 * Cross-tenant variant emitted by emitSyncConflict alongside the tenant
 * event — adds `tenant_id` so platform staff can attribute the alert.
 */
interface SyncConflictPlatformEventPayload extends SyncConflictEventPayload {
  tenant_id: string;
}

const PLATFORM_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);
const TENANT_REALTIME_ROLES = new Set(['OWNER', 'MANAGER']);

/**
 * Payload for the resolution event (mirrors emitSyncConflictResolved).
 */
interface SyncConflictResolvedPayload {
  conflict_id: string;
  entity_type: string;
  resolution: string;
  resolved_by: string;
}

interface SyncConflictResolvedPlatformPayload extends SyncConflictResolvedPayload {
  tenant_id: string;
}

export interface UseSyncConflictsRealtimeOptions {
  /** Called when the visible conflict list should refetch. */
  onRefresh: () => void;
  /**
   * Currently selected tenant in the picker (platform users only). When set,
   * platform events from a non-matching tenant fire a toast but skip the
   * list refresh. Pass `null` for tenant-scoped users (no picker).
   */
  selectedTenantId: string | null;
  /**
   * tenant_id → display name lookup, sourced from the existing tenant
   * picker cache on the page. Used to label cross-tenant toasts. Falls
   * back to a truncated UUID if a tenant is missing from the map.
   */
  tenantNameById: Map<string, string>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSyncConflictsRealtime(opts: UseSyncConflictsRealtimeOptions): void {
  const profile = useAuthStore((s) => s.profile);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Refs so the effect body can read the latest references without
  // re-binding the socket listener on every render. Mirrors the POS hook
  // pattern at apps/pos/src/hooks/use-sync-conflicts-realtime.ts:49-52.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const onRefreshRef = useRef(opts.onRefresh);
  onRefreshRef.current = opts.onRefresh;
  const selectedTenantIdRef = useRef(opts.selectedTenantId);
  selectedTenantIdRef.current = opts.selectedTenantId;
  const tenantNameByIdRef = useRef(opts.tenantNameById);
  tenantNameByIdRef.current = opts.tenantNameById;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    const role = profile?.role;
    if (!role) return;

    const isPlatform = PLATFORM_ROLES.has(role);
    const isTenantRealtime = TENANT_REALTIME_ROLES.has(role);
    if (!isPlatform && !isTenantRealtime) return;

    const eventName = isPlatform ? 'sync:conflict:platform' : 'sync:conflict';

    const unsubscribe = onSocketEvent<
      SyncConflictPlatformEventPayload | SyncConflictEventPayload
    >(eventName, (payload) => {
      // Defensive: drop malformed events rather than crashing the listener.
      if (!payload || typeof payload.conflict_id !== 'string' || !payload.entity_type) return;

      const isPlatformPayload =
        isPlatform && typeof (payload as SyncConflictPlatformEventPayload).tenant_id === 'string';

      // Decide whether to refresh the visible list:
      //   - tenant events  → always refresh (only one tenant in scope)
      //   - platform events → refresh only when no filter is set OR the event
      //     matches the currently picked tenant
      let shouldRefresh = true;
      if (isPlatformPayload) {
        const eventTenantId = (payload as SyncConflictPlatformEventPayload).tenant_id;
        const selected = selectedTenantIdRef.current;
        shouldRefresh = !selected || selected === eventTenantId;
      }
      if (shouldRefresh) {
        try {
          onRefreshRef.current();
        } catch {
          // Page may have unmounted between event arrival and dispatch.
          // Swallow — the next mount will reload from REST.
        }
      }

      // Build toast title/description.
      const entityLabel =
        ENTITY_TYPE_CONFIG[payload.entity_type]?.label ?? payload.entity_type;

      let title = `Sync Conflict (${entityLabel})`;
      if (isPlatformPayload) {
        const eventTenantId = (payload as SyncConflictPlatformEventPayload).tenant_id;
        const tenantLabel =
          tenantNameByIdRef.current.get(eventTenantId) ?? eventTenantId.slice(0, 8);
        title = `[${tenantLabel}] ${title}`;
      }

      toastRef.current({
        variant: 'warning',
        title,
        description: payload.message || 'A pending offline sync needs review.',
        action: {
          label: 'Review',
          onClick: () => {
            // No-op if already on /sync-health, else navigate over.
            if (pathnameRef.current !== '/sync-health') {
              navigateRef.current('/sync-health');
            }
          },
        },
      });
    });

    // Subscribe to resolution events so other viewers see updates in
    // real time (e.g. two platform staff on the same page). Mirrors
    // the dual-channel pattern: sync:conflict:resolved (tenant) and
    // sync:conflict:resolved:platform (platform staff).
    const resolvedEventName = isPlatform
      ? 'sync:conflict:resolved:platform'
      : 'sync:conflict:resolved';

    const unsubscribeResolved = onSocketEvent<
      SyncConflictResolvedPlatformPayload | SyncConflictResolvedPayload
    >(resolvedEventName, (payload) => {
      if (!payload || typeof payload.conflict_id !== 'string') return;

      const isPlatformPayload =
        isPlatform && typeof (payload as SyncConflictResolvedPlatformPayload).tenant_id === 'string';

      let shouldRefresh = true;
      if (isPlatformPayload) {
        const eventTenantId = (payload as SyncConflictResolvedPlatformPayload).tenant_id;
        const selected = selectedTenantIdRef.current;
        shouldRefresh = !selected || selected === eventTenantId;
      }
      if (shouldRefresh) {
        try {
          onRefreshRef.current();
        } catch {
          // Page may have unmounted — swallow.
        }
      }

      const entityLabel =
        ENTITY_TYPE_CONFIG[payload.entity_type]?.label ?? payload.entity_type;

      let title = `Conflict Resolved (${entityLabel})`;
      if (isPlatformPayload) {
        const eventTenantId = (payload as SyncConflictResolvedPlatformPayload).tenant_id;
        const tenantLabel =
          tenantNameByIdRef.current.get(eventTenantId) ?? eventTenantId.slice(0, 8);
        title = `[${tenantLabel}] ${title}`;
      }

      toastRef.current({
        variant: 'success',
        title,
        description: `Resolved as ${payload.resolution} by ${payload.resolved_by}`,
      });
    });

    return () => {
      unsubscribe();
      unsubscribeResolved();
    };
  }, [profile?.role]);
}
